// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import Popover from "@store/MobiusOverrides/Popover/Popover";
import Link from "@insite/mobius/Link";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import getColor from "@insite/mobius/utilities/getColor";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { contentWidthStyles, focusStyles, siteWidthStyles } from "@toolkit/Themes/common/mixins";
import * as cssLinter from "css";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { useDispatch } from "react-redux";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";

enum fields {
    tabs = "tabs",
    customCSS = "customCSS",
    inititalDepth = "initialDepth",
    title = "title",
    type = "type",
    url = "url",
    categories = "categories",
}
interface TabConfig {
    fields: {
        title: string;
        type: "link" | "category";
        link?: LinkFieldValue;
        openInNewTab?: boolean;
        categories?: string[];
    };
}

interface TabbedNavigationProps extends WidgetProps {
    fields: {
        initialDepth: number;
        tabs: TabConfig[];
        customCSS?: string;
    };
}
interface TabbedNavigationStyles {
    popoverContent?: InjectableCss;
    tabList?: InjectableCss;
    columnWrapper?: InjectableCss;
    headingLink?: LinkPresentationProps;
    verticalSeperator?: InjectableCss;
    subCategoryLink?: LinkPresentationProps;
    linkStyles?: LinkPresentationProps;
    buttonStyles?: LinkPresentationProps;
}

const tabbedNavigationStyles: TabbedNavigationStyles = {
    linkStyles: {
        css: css`
            padding: 0;
            margin: 0;
            padding: 0 ${({ theme }) => theme.design.layout.spacing.smaller};
            min-width: 100px;
            justify-content: center;
            height: 100%;
            border-radius: ${({ theme }) => theme.design.radius.small};
            color: ${getColor("common.background")};
            &:hover {
                background: ${getColor("common.background")};
                text-decoration: underline;
                color: ${getColor("primary")};
                outline: ${getColor("primary")} 2px solid;
                outline-offset: -2px;
            }
        `,
    },
    verticalSeperator: {
        css: css`
            width: 2px;
            background-color: ${getColor("common.border")};
            margin-top: 16px;
        `,
    },
    subCategoryLink: {
        css: css`
            ${linkStyles("text.main", "primary", false, true)}
        `,
    },
    headingLink: {
        css: css`
            ${linkStyles("primary", "secondary", false, true)}
            width: fit-content;
            margin-bottom: ${({ theme }) => theme.design.layout.spacing.smaller};
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    popoverContent: {
        css: css`
            display: flex;
            gap: ${({ theme }) => theme.design.layout.spacing.base};
            padding: ${({ theme }) => theme.design.layout.spacing.smaller};
            background: #fff;
            height: 100%;
            padding-bottom: 1.5rem;
            width: 100%;
            justify-content: space-between;
        `,
    },
    tabList: {
        css: css`
            display: flex;
            list-style: none;
            padding: 0;
            margin: 0;
            height: 100%;
            border-radius: ${({ theme }) => theme.design.radius.small};
            width: 100%;
        `,
    },
    columnWrapper: {
        css: css`
            padding: 16px;
            border-right: 1px solid #e0e0e0;
            &:last-child {
                border-right: none;
            }
        `,
    },
};

const defaultCustomCss = `& {
}

.tabbed-navigation-wrapper {
}

.tab-list-wrapper {
}

.tab-button {
}

.tab-button.active {
}

.popover-content-wrapper {
}

.category-column {
}

.category-heading-link {
}


.subcategory-link {
}
`;

interface PopoverRef extends HTMLUListElement {
    openPopover: () => void;
    closePopover: () => void;
}
const TabbedNavigation: React.FC<TabbedNavigationProps> = ({ fields: { tabs, customCSS, initialDepth } }) => {
    const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
    const [fetchedCategoryIds, setFetchedCategoryIds] = useState<string[]>([]);
    const styles = tabbedNavigationStyles;
    const popoverContentRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<PopoverRef>(null);
    const hasLoadedOnce = useRef(false);
    const timerId = useRef<number | null>(null);

    const dispatch = useDispatch();
    const handleTabClick = (index: number, type: string) => {
        if (activeTabIndex === index) {
            return;
        }
        if (type === "link") {
            handlePopoverClose();
            return;
        }
        if (timerId.current) {
            clearTimeout(timerId.current);
        }
        timerId.current = window.setTimeout(() => {
            timerId.current = null;
            if (popoverRef.current) {
                popoverRef.current.openPopover();
            }
        }, 333);
        setActiveTabIndex(index);
    };
    const handlePopoverClose = () => {
        popoverRef.current?.closePopover();
        setActiveTabIndex(null);
        if (timerId.current !== null) {
            clearTimeout(timerId.current);
            timerId.current = null;
        }
    };

    const categoriesById = useAppSelector(state => state.data.categories.byId);
    const haveAnyCategoriesLoaded = Object.keys(categoriesById).length > 0;
    const missingCategories = new Set(
        tabs.reduce<string[]>((acc, tab) => {
            if (tab.fields.type === "category") {
                tab.fields.categories?.forEach(categoryId => {
                    if (!categoriesById[categoryId] || !categoriesById[categoryId].subCategoryIds) {
                        acc.push(categoryId);
                    }
                });
            }
            return acc;
        }, []),
    );
    const loadingCategories = useAppSelector(state => state.data.categories.isLoading);
    const categoriesAreLoading = Object.keys(loadingCategories).length > 0;
    const links = tabs.reduce<LinkFieldValue[]>((acc, tab) => {
        if (tab.fields.type === "link" && tab.fields.link) {
            acc.push(tab.fields?.link);
        }
        return acc;
    }, []);
    const fixedLinks = useGetLinks(links, o => o);
    let lastLinkIndex = -1;
    const tabsWithLinkData = tabs.map(tab => {
        if (tab.fields.type === "link") {
            lastLinkIndex++;
            return {
                ...tab,
                fields: {
                    ...tab.fields,
                    link: fixedLinks[lastLinkIndex],
                },
            };
        }
        return tab;
    });

    useEffect(() => {
        if (!hasLoadedOnce.current) {
            hasLoadedOnce.current = true;
            dispatch(loadCategories({ includeStartCategory: true, maxDepth: initialDepth }));
            return;
        }
        if (categoriesAreLoading || !haveAnyCategoriesLoaded) {
            return;
        }

        let newFetchedCategoryIds = [...fetchedCategoryIds];
        missingCategories.forEach(categoryId => {
            if (!newFetchedCategoryIds.includes(categoryId)) {
                newFetchedCategoryIds.push(categoryId);
                dispatch(loadCategories({ includeStartCategory: true, startCategoryId: categoryId, maxDepth: 3 }));
            }
        });
        if (newFetchedCategoryIds.length > fetchedCategoryIds.length) {
            setFetchedCategoryIds(newFetchedCategoryIds);
        }
    }, [missingCategories, dispatch, categoriesAreLoading]);
    const TabButtons = (
        <StyledWrapper
            css={css`
                width: 100%;
                height: 100%;
                background-color: ${getColor("primary")};
                border-radius: 2px;
            `}
        >
            <StyledWrapper
                className="tab-list-wrapper"
                {...styles.tabList}
                onClick={event =>
                    event.stopPropagation()
                } /* to prevent opening the popover when a button isn't clicked*/
            >
                {tabsWithLinkData.map((tab, index) => {
                    const isActive = index === activeTabIndex;
                    const isLink = tab.fields.type === "link";
                    const link = tab.fields.link;
                    return (
                        <Link
                            key={tab.fields.title}
                            target={isLink && tab.fields.openInNewTab ? "_blank" : undefined}
                            onMouseEnter={() => handleTabClick(index, tab.fields.type)}
                            href={isLink && link && "url" in link ? link.url ?? "" : undefined}
                            css={css`
                                display: inline-flex;
                                flex-grow: 1;
                                align-items: center;
                                justify-content: center;
                                padding: 0 ${({ theme }) => theme.design.layout.spacing.smaller};
                                margin: 0;
                                font-weight: bold;
                                min-width: 100px;
                                height: 100%;
                                font-size: ${({ theme }) => theme.design.typography.size.small};
                                cursor: pointer;
                                transition: opacity 0.2s, box-shadow 0.2s, border-color 0.2s, color 0.2s,
                                    background-color 0.2s;

                                ${isLink && styles.linkStyles?.css}

                                ${!isLink &&
                                css`
                                    background: transparent;
                                    border: none;
                                    color: white;

                                    ${focusStyles()};

                                    ${isActive &&
                                    css`
                                        opacity: 1;
                                        color: ${getColor("primary.dark")};
                                        background: ${getColor("common.background")};
                                        outline: ${getColor("primary")} 2px solid !important;
                                        outline-offset: -2px;
                                        z-index: 1;
                                    `}
                                `}
                            `}
                            className={`tab-button${isActive ? " active" : ""}${isLink ? " link" : ""}`}
                        >
                            {tab.fields.title.toUpperCase()}
                        </Link>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );

    const activeTab = activeTabIndex !== null ? tabs[activeTabIndex].fields : null;
    return (
        <StyledWrapper
            css={css`
                ${customCSS || ""}
            `}
        >
            <StyledWrapper
                onMouseLeave={handlePopoverClose}
                css={css`
                    /* background: ${getColor("secondary")}; */
                    ${siteWidthStyles()};
                    height: 50px;
                `}
                className="tabbed-navigation-wrapper"
            >
                <Popover
                    ref={popoverRef}
                    isOpen={activeTabIndex !== null}
                    transitionDuration="short"
                    handleKeyDown={() => {}}
                    popoverTrigger={TabButtons}
                    onClose={() => setActiveTabIndex(null)}
                    onKeyDown={() => {}}
                    insideRefs={[popoverContentRef]}
                    positionFunction={() => ({ position: "absolute", top: -4, left: 0 })}
                    contentBodyProps={{
                        css: css`
                            position: static !important;
                            box-shadow: ${({ theme }) => theme.shadows["small"]};

                            width: 100%;
                            ${activeTabIndex === null ? "display: none;" : ""}
                        `,
                        as: "div",
                        _height: pxToRem(1200),
                    }}
                >
                    <StyledWrapper
                        {...styles.popoverContent}
                        ref={popoverContentRef}
                        className="popover-content-wrapper"
                    >
                        {activeTab?.categories?.map((categoryId, index) => {
                            const category = categoriesById[categoryId];
                            const numOfSubCategories = activeTab.categories?.length ?? 0;
                            const isLastCategory = numOfSubCategories - 1 === index;
                            const isFirstCategory = index === 0;
                            if (!category) {
                                return null;
                            }

                            return (
                                <>
                                    {isFirstCategory && numOfSubCategories > 1 && <StyledWrapper />}
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            height: 100%;
                                            gap: ${({ theme }) => theme.design.layout.spacing.tiny};
                                        `}
                                        key={categoryId}
                                        className="category-column"
                                    >
                                        <Link
                                            {...styles.headingLink}
                                            href={category?.path}
                                            className="category-heading-link"
                                            onClick={handlePopoverClose}
                                        >
                                            {category.shortDescription.toLocaleUpperCase()}
                                        </Link>
                                        {category.subCategoryIds?.map(subCategoryId => {
                                            const subCategory = categoriesById[subCategoryId];
                                            if (!subCategory) {
                                                return null;
                                            }
                                            return (
                                                <Link
                                                    {...styles.subCategoryLink}
                                                    key={subCategoryId}
                                                    href={subCategory?.path}
                                                    className="subcategory-link"
                                                    onClick={handlePopoverClose}
                                                >
                                                    {subCategory.shortDescription}
                                                </Link>
                                            );
                                        })}
                                    </StyledWrapper>
                                    {!isLastCategory ? (
                                        <StyledWrapper {...styles.verticalSeperator} />
                                    ) : (
                                        <StyledWrapper />
                                    )}
                                </>
                            );
                        })}
                    </StyledWrapper>
                </Popover>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: TabbedNavigation,
    definition: {
        group: "Header",
        icon: "Tabs",
        displayName: "Tabbed Category Navigation",
        fieldDefinitions: [
            {
                name: "initialDepth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                displayName: "Category Depth",
                tooltip: "The depth of categories to load when the widget is first rendered.",
                min: 1,
                max: 4,
                isRequired: true,
                fieldType: "General",
            },
            {
                name: "tabs",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Translatable",
                getDisplay: (item: HasFields) => item.fields.title || "Untitled Tab",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "type",
                        editorTemplate: "DropDownField",
                        defaultValue: "link",
                        options: [
                            { displayName: "Link", value: "link" },
                            { displayName: "Category", value: "category" },
                        ],
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => item.fields.type === "link",
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: item => item.fields.type === "link",
                    },
                    {
                        name: "categories",
                        editorTemplate: "CategoryPickerField" as any,
                        defaultValue: [],
                        getDisplay: (item: HasFields) => item.fields.title || "Untitled Category",
                        isVisible: (item: HasFields) => item.fields.type === "category",
                    },
                ],
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: { displayName: "Advanced", sortOrder: 1 },
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (!value) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
